// src/pages/ProductsPage.jsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import ProductCard from '../components/ProductCard';
import products from '../data/products';
import './ProductsPage.css';

const ProductsPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStrain, setSelectedStrain] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('default');
  const [priceRange, setPriceRange] = useState({ min: 0, max: 1000 });
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [isFilterSticky, setIsFilterSticky] = useState(false);
  const productsPerPage = 12;
  
  // Ref for filter bar to scroll to
  const filterBarRef = useRef(null);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);

  // Function to scroll to filter bar
  const scrollToFilters = useCallback(() => {
    if (filterBarRef.current) {
      const yOffset = -20; // Offset to account for padding
      const y = filterBarRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
      
      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
    }
  }, []);

  // Handle filter bar sticky effect
  useEffect(() => {
    const handleScroll = () => {
      if (filterBarRef.current) {
        const filterBarTop = filterBarRef.current.offsetTop;
        setIsFilterSticky(window.scrollY > filterBarTop - 80);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Get unique categories and strains with counts
  const categories = useMemo(() => {
    const catMap = new Map();
    products.forEach(p => {
      catMap.set(p.category, (catMap.get(p.category) || 0) + 1);
    });
    return [['all', products.length], ...Array.from(catMap.entries())];
  }, []);

  const strains = useMemo(() => {
    const strainMap = new Map();
    products.forEach(p => {
      if (p.strain) {
        strainMap.set(p.strain, (strainMap.get(p.strain) || 0) + 1);
      }
    });
    return [['all', products.length], ...Array.from(strainMap.entries())];
  }, []);

  // Filter and sort products
  const filteredProducts = useMemo(() => {
    let filtered = products.filter(product => {
      const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
      const matchesStrain = selectedStrain === 'all' || product.strain === selectedStrain;
      const matchesSearch = searchTerm === '' || 
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.strain && product.strain.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const productPrice = product.priceOptions?.[0]?.price || product.price || 0;
      const matchesPrice = productPrice >= priceRange.min && productPrice <= priceRange.max;
      
      return matchesCategory && matchesStrain && matchesSearch && matchesPrice;
    });

    // Apply sorting
    switch(sortBy) {
      case 'price-low':
        filtered.sort((a, b) => {
          const priceA = a.priceOptions?.[0]?.price || a.price || 0;
          const priceB = b.priceOptions?.[0]?.price || b.price || 0;
          return priceA - priceB;
        });
        break;
      case 'price-high':
        filtered.sort((a, b) => {
          const priceA = a.priceOptions?.[0]?.price || a.price || 0;
          const priceB = b.priceOptions?.[0]?.price || b.price || 0;
          return priceB - priceA;
        });
        break;
      case 'name-asc':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name-desc':
        filtered.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'rating':
        filtered.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      default:
        // Default sorting (featured)
        filtered.sort((a, b) => (b.popular ? 1 : 0) - (a.popular ? 1 : 0));
    }

    return filtered;
  }, [selectedCategory, selectedStrain, searchTerm, sortBy, priceRange]);

  // Pagination
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
    // Scroll to filters when filters change (optional)
    scrollToFilters();
  }, [selectedCategory, selectedStrain, searchTerm, sortBy, priceRange, scrollToFilters]);

  const clearFilters = useCallback(() => {
    setSelectedCategory('all');
    setSelectedStrain('all');
    setSearchTerm('');
    setSortBy('default');
    setPriceRange({ min: 0, max: 1000 });
    // Scroll to filters after clearing
    setTimeout(scrollToFilters, 100);
  }, [scrollToFilters]);

  // Handle page change with scroll to top
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    // Scroll to top of products when page changes
    if (filterBarRef.current) {
      const yOffset = -20;
      const y = filterBarRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
    }
  }, []);

  return (
    <div className="products-page">
      {/* Filter Bar - Now at the top */}
      <div 
        ref={filterBarRef}
        className={`filter-bar ${isFilterSticky ? 'sticky' : ''}`}
      >
        <div className="container">
          <div className="filter-bar-content">
            <div className="filter-bar-left">
              <button 
                className="filter-toggle"
                onClick={() => setShowFilters(!showFilters)}
                aria-expanded={showFilters}
              >
                <svg className="filter-icon" viewBox="0 0 24 24" fill="none">
                  <path d="M3 4h18M3 12h18M3 20h18M8 8v8M16 8v8" 
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <span>Filters</span>
                {(selectedCategory !== 'all' || selectedStrain !== 'all' || searchTerm || 
                  priceRange.min > 0 || priceRange.max < 1000) && (
                  <span className="filter-badge">
                    {[
                      selectedCategory !== 'all' ? 1 : 0,
                      selectedStrain !== 'all' ? 1 : 0,
                      searchTerm ? 1 : 0,
                      (priceRange.min > 0 || priceRange.max < 1000) ? 1 : 0
                    ].reduce((a, b) => a + b, 0)}
                  </span>
                )}
              </button>

              <div className="view-options">
                <button 
                  className={`view-option ${viewMode === 'grid' ? 'active' : ''}`}
                  onClick={() => setViewMode('grid')}
                  aria-label="Grid view"
                >
                  <svg viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
                    <rect x="14" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
                    <rect x="3" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
                    <rect x="14" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </button>
                <button 
                  className={`view-option ${viewMode === 'list' ? 'active' : ''}`}
                  onClick={() => setViewMode('list')}
                  aria-label="List view"
                >
                  <svg viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="3" width="18" height="4" stroke="currentColor" strokeWidth="2"/>
                    <rect x="3" y="10" width="18" height="4" stroke="currentColor" strokeWidth="2"/>
                    <rect x="3" y="17" width="18" height="4" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </button>
              </div>
            </div>

            <div className="filter-bar-right">
              <select 
                className="sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                aria-label="Sort by"
              >
                <option value="default">Featured</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="name-asc">Name: A to Z</option>
                <option value="name-desc">Name: Z to A</option>
                <option value="rating">Top Rated</option>
              </select>

              <p className="results-count">
                <span className="count-number">{filteredProducts.length}</span>
                <span className="count-label">products</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <section className="products-section">
        <div className="container">
          <div className="products-layout">
            {/* Filters Sidebar */}
            <aside className={`filters-sidebar ${showFilters ? 'open' : ''}`}>
              <div className="filters-header">
                <h3>Filters</h3>
                <button className="close-filters" onClick={() => setShowFilters(false)}>
                  ✕
                </button>
              </div>

              {/* Search */}
              <div className="filter-group">
                <label className="filter-label">
                  <svg className="filter-icon-small" viewBox="0 0 24 24" fill="none">
                    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2"/>
                    <path d="M16 16L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  Search Products
                </label>
                <input
                  type="text"
                  placeholder="Search by name, description, strain..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>

              {/* Categories */}
              <div className="filter-group">
                <label className="filter-label">Categories</label>
                <div className="filter-options">
                  {categories.map(([cat, count]) => (
                    <button
                      key={cat}
                      className={`filter-chip ${selectedCategory === cat ? 'active' : ''}`}
                      onClick={() => setSelectedCategory(cat)}
                    >
                      <span className="chip-label">
                        {cat === 'all' ? 'All Categories' : cat.replace('-', ' ')}
                      </span>
                      <span className="chip-count">{count}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Strains */}
              <div className="filter-group">
                <label className="filter-label">Strain Type</label>
                <div className="filter-options">
                  {strains.map(([strain, count]) => (
                    <button
                      key={strain}
                      className={`filter-chip ${selectedStrain === strain ? 'active' : ''}`}
                      onClick={() => setSelectedStrain(strain)}
                    >
                      <span className="chip-label">
                        {strain === 'all' ? 'All Strains' : strain}
                      </span>
                      <span className="chip-count">{count}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Price Range */}
              <div className="filter-group">
                <label className="filter-label">Price Range ($)</label>
                <div className="price-range">
                  <div className="price-inputs">
                    <input
                      type="number"
                      value={priceRange.min}
                      onChange={(e) => setPriceRange({...priceRange, min: Number(e.target.value)})}
                      placeholder="Min"
                      min="0"
                    />
                    <span className="price-separator">-</span>
                    <input
                      type="number"
                      value={priceRange.max}
                      onChange={(e) => setPriceRange({...priceRange, max: Number(e.target.value)})}
                      placeholder="Max"
                      min="0"
                    />
                  </div>
                </div>
              </div>

              {/* Clear Filters */}
              <button className="clear-filters" onClick={clearFilters}>
                Clear All Filters
              </button>
            </aside>

            {/* Products Grid/List */}
            <main className="products-main">
              {/* Page Title - Simple and Clean */}
              <div className="page-header">
                <h1 className="page-title">All Products</h1>
                <p className="page-description">
                  Browse our complete selection of premium cannabis products
                </p>
              </div>

              {filteredProducts.length > 0 ? (
                <>
                  <div className={`products-container ${viewMode}`}>
                    {currentProducts.map(product => (
                      <ProductCard key={product.id} product={product} />
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="pagination">
                      <button
                        className="pagination-btn"
                        onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
                        disabled={currentPage === 1}
                      >
                        <svg viewBox="0 0 24 24" fill="none">
                          <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </button>
                      
                      <div className="pagination-pages">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          
                          return (
                            <button
                              key={pageNum}
                              className={`pagination-page ${currentPage === pageNum ? 'active' : ''}`}
                              onClick={() => handlePageChange(pageNum)}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>

                      <button
                        className="pagination-btn"
                        onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
                        disabled={currentPage === totalPages}
                      >
                        <svg viewBox="0 0 24 24" fill="none">
                          <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="no-results">
                  <div className="no-results-icon">🔍</div>
                  <h2>No products found</h2>
                  <p>Try adjusting your search or filters</p>
                  <button className="reset-btn" onClick={clearFilters}>
                    Reset Filters
                  </button>
                </div>
              )}
            </main>
          </div>
        </div>
      </section>

      {/* Overlay for mobile filters */}
      {showFilters && <div className="filters-overlay" onClick={() => setShowFilters(false)} />}
      
      {/* Back to Top Button */}
      <button
        className={`back-to-top ${isFilterSticky ? 'visible' : ''}`}
        onClick={() => {
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }}
        aria-label="Back to top"
      >
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M12 19V5M5 12l7-7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      </button>
    </div>
  );
};

export default ProductsPage;